import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { UseFormControllerReturn } from "formifex/dist/components/FormController";
import useRequiredFields from "../../hooks/useRequiredFields";
import useWindowDimensions from "../../hooks/useWindowDimensions";

interface FormValidationStatusProps {
	form: UseFormControllerReturn;
	reportType?: string;
	mode?: string;
}

const BREAKPOINT = 1200;

const FormValidationStatus = ({
	form,
	reportType,
	mode,
}: FormValidationStatusProps) => {
	const { unfilledCount, isComplete } = useRequiredFields(form, reportType);
	const [hasInteracted, setHasInteracted] = useState(false);
	const initSnapshotRef = useRef<string | null>(null);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { width } = useWindowDimensions();
	const showFullText = width >= BREAKPOINT;

	useEffect(() => {
		// Reset state when form changes (new form opened)
		setHasInteracted(false);
		initSnapshotRef.current = null;

		// Wait for form initialization to complete (schema defaults applied)
		// Form controller uses 1500ms timeout, we use 2000ms to be safe
		timerRef.current = setTimeout(() => {
			// Capture snapshot after initialization is complete
			initSnapshotRef.current = JSON.stringify(form?.formData);
		}, 2000);

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reportType]);

	useEffect(() => {
		if (!initSnapshotRef.current || !form?.formData) {
			return;
		}

		const currentSnapshot = JSON.stringify(form.formData);
		if (currentSnapshot !== initSnapshotRef.current) {
			setHasInteracted(true);
		}
	}, [form?.formData]);

	const message = useMemo(() => {
		// Don't show initial message when editing existing reports
		if (!hasInteracted) {
			if (mode === "edit") {
				return isComplete
					? "All required fields completed"
					: `${unfilledCount} required field${unfilledCount !== 1 ? "s" : ""} remaining.`;
			}
			return "Complete required field to submit";
		}

		if (isComplete) {
			return "All required fields completed";
		}

		return `${unfilledCount} required field${unfilledCount !== 1 ? "s" : ""} remaining.`;
	}, [hasInteracted, isComplete, unfilledCount, mode]);

	if (showFullText) {
		return (
			<span
				className="flex items-center gap-1.5"
				style={{
					color: isComplete
						? "var(--success-color, '#00FF75')"
						: "var(--text-color-secondary, 'rgba(255, 255, 255, 0.65)')",
					fontSize: "14px",
					whiteSpace: "nowrap",
				}}
			>
				<InfoCircleOutlined />
				{message}
			</span>
		);
	}

	return (
		<Tooltip title={message}>
			<span
				style={{
					color: isComplete
						? "var(--success-color, '#00FF75')"
						: "var(--text-color-secondary, 'rgba(255, 255, 255, 0.65)')",
					fontSize: "14px",
					cursor: "help",
				}}
			>
				<InfoCircleOutlined />
			</span>
		</Tooltip>
	);
};

export default FormValidationStatus;
